import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import Logo from "@/components/Logo";

/**
 * Thank You page — Spanish only (forced "ar" locale), reachable at /thank_you.
 */
export default function ThankYou() {
  const { t } = useTranslation();
  const tr = (key, options = {}) => t(key, { lng: "ar", ...options });
  const nextItems = tr("thank_you.next_items", { returnObjects: true }) || [];

  useEffect(() => {
    document.documentElement.lang = "es-AR";
    document.title = tr("thank_you.meta_title");
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App" data-testid="thank-you-page">
      <header className="site-header border-b border-border bg-background">
        <div className="container-page flex h-[72px] items-center">
          <Link to="/" aria-label="Litiosur" data-testid="thank-you-logo-link">
            <Logo title={tr("brand.tagline")} />
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <div className="strata-band h-1.5 w-full" aria-hidden="true" />
        <div className="container-page py-16 sm:py-24">
          <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card p-7 shadow-[0_10px_30px_hsl(214_30%_10%/0.08)] sm:p-10">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[hsl(var(--terracotta)/0.12)]">
              <CheckCircle2 className="h-6 w-6 text-[hsl(var(--terracotta))]" />
            </span>
            <p className="font-data mt-6 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--ink-3))]">
              {tr("thank_you.eyebrow")}
            </p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-[38px]">
              {tr("thank_you.title")}
            </h1>
            <p className="mt-5 text-sm leading-relaxed text-[hsl(var(--ink-2))] sm:text-base">
              {tr("thank_you.p1")}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {tr("thank_you.p2")}
            </p>

            <Separator className="my-8" />

            <h2 className="font-data text-[11px] uppercase tracking-[0.16em] text-[hsl(var(--ink-3))]">
              {tr("thank_you.next_title")}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {nextItems.map((item) => (
                <li
                  key={item}
                  className="border-l-2 border-border pl-3 text-sm leading-relaxed text-muted-foreground"
                >
                  {item}
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                asChild
                className="h-12 rounded-lg px-6 text-sm"
                data-testid="thank-you-back-home-button"
              >
                <Link to="/">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {tr("thank_you.back_home")}
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 rounded-lg border-border px-6 text-sm"
                data-testid="thank-you-email-button"
              >
                <a href="mailto:consultas@litiosur.com?subject=Consulta%20Litiosur">
                  <Mail className="mr-2 h-4 w-4" />
                  {tr("thank_you.email_label")} consultas@litiosur.com
                </a>
              </Button>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-border bg-[hsl(var(--surface-2))]">
        <div className="container-page flex flex-col gap-2 py-8 text-xs text-muted-foreground">
          <p>{tr("footer.legal.company")}</p>
          <p>{tr("footer.legal.cuit")}</p>
          <p>{tr("footer.address_legal")}</p>
          <p className="mt-2">
            © {new Date().getFullYear()} {tr("footer.rights")}
          </p>
        </div>
      </footer>
    </div>
  );
}
